import { useState, useEffect } from 'react';

interface EditTimeEntryFormProps {
  entry: {
    _id: string;
    userId: string;
    startTime: string;
    endTime?: string;
    pausedDuration: number;
    type?: 'work' | 'sick' | 'vacation';
    adminEditComment?: string;
  };
  onClose: () => void;
  submitLabel?: string;
  title?: string;
  confirmMessage?: string;
  onSave: (data: {
    id: string;
    startTime: string;
    endTime: string;
    pausedDuration: number;
    adminEditComment?: string;
  }) => void;
}

// ✅ Hilfsfunktion: UTC → lokaler Wert für <input type="datetime-local">
const toLocalInputValue = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const tzOffset = date.getTimezoneOffset() * 60000; // Offset in ms
  const localISO = new Date(date.getTime() - tzOffset).toISOString();
  return localISO.slice(0, 16); // YYYY-MM-DDTHH:mm
};

// ✅ Hilfsfunktion: lokaler Wert → UTC ISO
const toUtcISOString = (localValue: string) => {
  if (!localValue) return '';
  const date = new Date(localValue);
  return date.toISOString();
};

function EditTimeEntryForm({
  entry,
  onClose,
  onSave,
  submitLabel = 'Speichern',
  title = 'Eintrag bearbeiten',
  confirmMessage = 'Willst du diesen Eintrag wirklich speichern?',
}: EditTimeEntryFormProps) {
  const isWorkEntry = !entry.type || entry.type === 'work';
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [pausedMinutes, setPausedMinutes] = useState(0);
  const [adminEditComment, setAdminEditComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setStartTime(toLocalInputValue(entry.startTime));
    setEndTime(entry.endTime ? toLocalInputValue(entry.endTime) : '');
    setPausedMinutes(Math.floor(entry.pausedDuration / 60));
    setAdminEditComment(entry.adminEditComment || '');
  }, [entry]);

  const handleSave = async () => {
    if (!startTime || !endTime) {
      setError('Bitte Start- und Endzeit ausfüllen.');
      return;
    }
    if (new Date(startTime) > new Date(endTime)) {
      setError('Startzeit darf nicht nach Endzeit liegen.');
      return;
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setError('');
    setLoading(true);

    try {
      await onSave({
        id: entry._id,
        startTime: toUtcISOString(startTime), // ✅ zurück nach UTC
        endTime: toUtcISOString(endTime),
        pausedDuration: isWorkEntry ? pausedMinutes * 60 : 0,
        adminEditComment,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="card p-4 shadow-sm" style={{ maxWidth: 400, margin: 'auto' }}>
        <h4 className="mb-3">{title}</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Startzeit */}
        <div className="mb-2">
          <label htmlFor="start-time" className="form-label">Startzeit*</label>
          <input
            id="start-time"
            type="datetime-local"
            className="form-control"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Endzeit */}
        <div className="mb-2">
          <label htmlFor="end-time" className="form-label">Endzeit*</label>
          <input
            id="end-time"
            type="datetime-local"
            className="form-control"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            disabled={loading}
          />
        </div>

        {isWorkEntry && (
          <div className="mb-3">
            <label htmlFor="paused-minutes" className="form-label">Pausenminuten</label>
            <input
              id="paused-minutes"
              type="number"
              min={0}
              className="form-control"
              value={pausedMinutes}
              onChange={(e) => setPausedMinutes(parseInt(e.target.value) || 0)}
              disabled={loading}
            />
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="admin-edit-comment" className="form-label">Kommentar</label>
          <textarea
            id="admin-edit-comment"
            className="form-control"
            rows={2}
            value={adminEditComment}
            onChange={(e) => setAdminEditComment(e.target.value)}
            disabled={loading}
            placeholder="Optionaler Hinweis zur Bearbeitung"
          />
        </div>

        <div className="d-flex justify-content-between">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Speichern...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditTimeEntryForm;
